import React from "react";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    IconButton,
    Typography,
} from "@mui/material";
import PlusIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { GACartItem } from "../../models/Cart";
import { priceFormatter } from "../../utils/helpers";
import AuthStore from "../../stores/AuthStore";
import { observer } from "mobx-react-lite";
import GroupPricingUpsellMessage from "./GroupPricingUpsell";

type Props = {
    item: GACartItem;
    onIncrement: () => void;
    onDecrement: () => void;
    onRemove: () => void;
    onSetQuantity: (quantity: number) => void;
    onRemoveRedemptionCode: () => void;
};

const CartRow = observer(
    ({
        item,
        onIncrement,
        onDecrement,
        onRemove,
        onSetQuantity,
        onRemoveRedemptionCode,
    }: Props) => {
        const getPriceNode = (): React.ReactNode => {
            if (item.comp) {
                return <Chip color="warning" label="COMP" size="small" />;
            }
            if (item.overridePrice) {
                return (
                    <Typography variant="body2">
                        {priceFormatter(item.basePrice ?? item.overridePrice)}
                    </Typography>
                );
            }
            if ((item.basePrice ?? item.department.price) === 0) {
                return <Chip color="success" label="FREE" size="small" />;
            }
            if (item.redemption) {
                return (
                    <Chip
                        color="success"
                        size="small"
                        label={"Code: " + item.redemption.code}
                        onDelete={onRemoveRedemptionCode}
                    />
                );
            }
            return (
                <Typography variant="body2">
                    {priceFormatter(item.basePrice ?? item.department.price)}
                </Typography>
            );
        };

        const getDiscountNode = (): React.ReactNode => {
            if (item.comp) return null;
            if (item.overridePrice !== null) {
                return (
                    <Typography variant="body2" color="success.main">
                        {priceFormatter(
                            item.department.price - item.overridePrice,
                        )}{" "}
                        in discounts ea.
                    </Typography>
                );
            }
            if (item.discount) {
                return (
                    <Typography variant="body2" color="success.main">
                        {priceFormatter(
                            item.discount.grossAmount ?? item.discount.amount,
                        )}{" "}
                        in discounts
                        {item.quantity > 1 ? " ea." : ""}
                    </Typography>
                );
            }
            return null;
        };

        const getServiceFeeNode = (): React.ReactNode => {
            if (item.comp) return null;
            return item.serviceFees.map((it) => (
                <Typography variant="body2" color="text.secondary" key={it.id}>
                    + {priceFormatter(it.amount)} {it.name} ea.
                </Typography>
            ));
        };


        // Must be between 0 and 500
        const onEnterCustomQuantity = () => {
            const quantity = window.prompt(
                "Enter custom quantity (1-300)",
                item.quantity.toString(),
            );
            if (quantity) {
                const parsedQuantity = parseInt(quantity);
                if (parsedQuantity >= 1 && parsedQuantity <= 300) {
                    onSetQuantity(parsedQuantity);
                } else {
                    alert("Please enter a number between 1 and 300");
                }
            }
        };

        return (
            <Card>
                <CardContent>
                    <Typography
                        variant="body2"
                        color="primaryText.main"
                        fontWeight={600}
                    >
                        {item.department.name}
                    </Typography>
                    {item.event.title &&
                        item.event.title !== item.department.name && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.25 }}
                            >
                                {item.event.title}
                            </Typography>
                        )}
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={1}>
                        <Grid item xs={7}>
                            {getPriceNode()}
                            {getServiceFeeNode()}
                            {getDiscountNode()}
                        </Grid>
                        <Grid
                            item
                            xs={2.5}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: "5px",
                                }}
                            >
                                {item.acceptsModifications ? (
                                    <>
                                        <IconButton
                                            onClick={onDecrement}
                                            size="small"
                                            color="error"
                                        >
                                            <RemoveIcon fontSize="small" />
                                        </IconButton>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                cursor: AuthStore.boxoffice
                                                    ? "pointer"
                                                    : "default",
                                            }}
                                            onClick={
                                                AuthStore.boxoffice
                                                    ? onEnterCustomQuantity
                                                    : undefined
                                            }
                                        >
                                            {item.quantity}
                                        </Typography>
                                        <IconButton
                                            onClick={onIncrement}
                                            disabled={
                                                !AuthStore.boxoffice &&
                                                item.quantity >=
                                                    item.department
                                                        .maxTicketsInCart
                                            }
                                            size="small"
                                            color="success"
                                        >
                                            <PlusIcon fontSize="small" />
                                        </IconButton>
                                    </>
                                ) : (
                                    <Typography variant="body2">
                                        {item.quantity}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                        <Grid
                            item
                            xs={2.5}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Typography variant="body2">
                                {priceFormatter(item.totalAmount)}
                            </Typography>
                        </Grid>
                    </Grid>
                    {!item.comp && (
                        <GroupPricingUpsellMessage
                            departmentId={item.department.id}
                            quantity={item.quantity}
                            sx={{ mt: 2 }}
                        />
                    )}
                </CardContent>
            </Card>
        );
    },
);

export default CartRow;
